/**
 * `EduhousenVerkkokoulutusRoute` — bespoke route for the
 * `/eduhousen-verkkokoulutus` URL (TA20.3, Phase A PR C.1).
 *
 * Lift posture: prose-into-seed; consent gate stays in page (per
 * `phase-a-content-inventory.md` §"Verified inventory"). The article's
 * static prose lifts into `body_html`; the consent checkbox + outbound
 * CTA stay as page-level chrome around the body.
 *
 * Composition (amended 2026-05-11 per D-PD-003 — the per-article
 * demo-disclosure aside was removed during the proto-parity
 * convergence sweep; demo identity is now carried at the route boundary,
 * see `docs/governance/proto-divergences.md` D-PD-003):
 *
 *   1. H1 carrying `article.title`.
 *   2. Body via `SanitizedHtml` (FR-049) — no `dangerouslySetInnerHTML`
 *      outside the wrapper.
 *   3. Consent gate: a `<label>` wraps a real `<input type="checkbox">`
 *      (so axe-core sees a programmatic label, not a synthetic one) and
 *      `metadata.consentLabelFi` text. Local component state
 *      (`useState<boolean>`) holds the value.
 *   4. CTA: when consent is checked, an `<a target="_blank"
 *      rel="noopener noreferrer">` to `metadata.outboundUrl`; when
 *      consent is unchecked, a `<button type="button" disabled>`. The
 *      conditional render is the honest disabled-anchor pattern — an
 *      `aria-disabled` anchor is a keyboard-navigation footgun (the
 *      anchor still receives focus and Enter still navigates).
 *
 * State matrix (mirrors `ArticleDetailPage`):
 *
 *   - `isPending` → loading skeleton.
 *   - `isError` → re-throw so the caller-mounted `PortErrorBoundary`
 *     catches it.
 *   - `data === null` → not-found surface.
 *   - `data` is an `Article` → populated render.
 *
 * Metadata narrowing
 * ==================
 *
 * `Article.metadata` is `Record<string, unknown>` at the port boundary.
 * The route narrows to `EduhousenMetadata` (from
 * `@omaplus/shared-types/content-metadata`) via a small type guard. A
 * shape violation (missing field, wrong type) is a contract violation;
 * the route throws a plain `Error` so the caller-mounted
 * `PortErrorBoundary` surfaces the diagnostic — silently rendering with
 * broken metadata would hide a seed-data bug behind a partially-rendered
 * page.
 */

import type { EduhousenMetadata } from '@omaplus/shared-types/content-metadata';
import { type JSX, useState } from 'react';
import { AnalyticsRouteReady } from '../../analytics/AnalyticsCaptureProvider.js';
import { NotFound } from '../../routes/NotFound.js';
import type { Article } from '../ContentReaderPort.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { useArticle } from '../hooks/useArticle.js';
import { ArticleContentBlocks } from './ArticleContentBlocks.js';
import {
  bodyClasses,
  DetailSurfaceShell,
  DetailSurfaceSkeleton,
  headingClasses,
} from './articleDetailSurface.js';
import { DetailSurfaceHero } from './detailSurfaceHero.js';

const ARTICLE_SLUG = 'eduhousen-verkkokoulutus';

const consentRowClasses = 'flex items-start gap-2 text-sm text-foreground';
const consentInputClasses = 'mt-0.5 h-4 w-4 cursor-pointer';
const ctaButtonClasses =
  'inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50';
const ctaAnchorClasses =
  'inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90';

/**
 * Route-specific Finnish CTA label. Co-located with the route per
 * `copy.ts`'s file-header policy: route-specific copy stays inline;
 * `copy.ts` is reserved for cross-route shared strings.
 */
const CTA_LABEL_COPY: string = 'Siirry Eduhouse-palveluun';

/**
 * Type-guard narrowing arbitrary `unknown` to `EduhousenMetadata`. The
 * three required fields each carry a type check; a missing field or a
 * wrong-typed field returns `false`, and the caller throws so the
 * `PortErrorBoundary` surfaces the diagnostic.
 *
 * Takes `unknown` (not `Record<string, unknown>`) because TS type
 * predicates require the predicate's type to be assignable to the
 * parameter's type, and the metadata interface uses `readonly`
 * properties that are NOT assignable to `Record<string, unknown>`'s
 * index signature.
 */
function isEduhousenMetadata(value: unknown): value is EduhousenMetadata {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record['outboundUrl'] === 'string' &&
    typeof record['consentLabelFi'] === 'string' &&
    typeof record['consentRequired'] === 'boolean'
  );
}

export function EduhousenVerkkokoulutusRoute(): JSX.Element {
  const query = useArticle(ARTICLE_SLUG);

  if (query.isPending) {
    return <DetailSurfaceSkeleton testId="eduhousen-route-skeleton" />;
  }

  if (query.isError) {
    // Re-throw so the wrapping `PortErrorBoundary` catches it. Mirrors
    // `ArticleDetailPage`'s posture — the error is an adapter / network
    // failure, not a not-found state.
    throw (
      query.error ?? new Error('useArticle resolved to an error state without an Error instance')
    );
  }

  if (query.data === null) {
    return <NotFound />;
  }

  const article = query.data;
  if (!isEduhousenMetadata(article.metadata)) {
    // A seed-shape violation. Throwing surfaces the diagnostic via the
    // caller-mounted `PortErrorBoundary` rather than rendering a
    // partially-broken page that hides the contract violation.
    throw new Error(
      `EduhousenVerkkokoulutusRoute: article "${ARTICLE_SLUG}" metadata does not match EduhousenMetadata shape`,
    );
  }
  const metadata = article.metadata;

  return (
    <PopulatedView
      title={article.title}
      imageUrl={article.imageUrl}
      imageAlt={article.imageAlt}
      bodyHtml={article.bodyHtml}
      contentBlocks={article.contentBlocks}
      metadata={metadata}
    />
  );
}

interface PopulatedViewProps {
  readonly title: string;
  readonly imageUrl: string | null;
  readonly imageAlt: string | null;
  readonly bodyHtml: string;
  readonly contentBlocks?: Article['contentBlocks'];
  readonly metadata: EduhousenMetadata;
}

/**
 * Populated branch — extracted into its own component so the
 * `useState` hook lives below the early-return state-matrix branches
 * (React forbids conditional hook calls). The eligibility / consent
 * routes follow this same shape (early-returns first, hook-bearing
 * branch last) so React's "rules of hooks" stay clean.
 *
 * `metadata.consentRequired` controls whether the consent gate fires.
 * Phase A's seed sets it to `true`, but the field is the documented
 * escape hatch for a future revision that lifts the gate without
 * changing `body_html`. Honoring the field at render time makes the
 * documented contract real: setting `consentRequired: false` in the
 * seed (or a future authored revision) skips the checkbox + button
 * pair and shows an enabled outbound anchor immediately.
 *
 * `useState<boolean>(false)` is called unconditionally so the rules of
 * hooks hold regardless of which branch the metadata picks; the state
 * is simply unused when the gate is bypassed.
 */
function PopulatedView({
  title,
  imageUrl,
  imageAlt,
  bodyHtml,
  contentBlocks,
  metadata,
}: PopulatedViewProps): JSX.Element {
  const [consented, setConsented] = useState<boolean>(false);
  const gateActive = metadata.consentRequired;
  const ctaEnabled = !gateActive || consented;

  return (
    <DetailSurfaceShell as="article">
      <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'eduhouse-online-training' }} />
      <DetailSurfaceHero imageUrl={imageUrl} mediaLabel={title} imageAlt={imageAlt} />
      <h1 className={headingClasses}>{title}</h1>
      {contentBlocks !== undefined ? (
        <ArticleContentBlocks contentBlocks={contentBlocks} />
      ) : (
        <SanitizedHtml as="div" className={bodyClasses} html={bodyHtml} />
      )}
      {gateActive ? (
        <label className={consentRowClasses}>
          <input
            type="checkbox"
            className={consentInputClasses}
            checked={consented}
            onChange={(event) => setConsented(event.target.checked)}
          />
          <span>{metadata.consentLabelFi}</span>
        </label>
      ) : null}
      {ctaEnabled ? (
        <a
          className={ctaAnchorClasses}
          href={metadata.outboundUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {metadata.ctaLabelFi ?? CTA_LABEL_COPY}
        </a>
      ) : (
        <button type="button" className={ctaButtonClasses} disabled>
          {metadata.ctaLabelFi ?? CTA_LABEL_COPY}
        </button>
      )}
    </DetailSurfaceShell>
  );
}
